import { motion } from 'framer-motion';
import { Stethoscope, Truck, ShieldCheck, Lock } from 'lucide-react';

const phases = [
  {
    num: '01',
    icon: Stethoscope,
    title: 'Book',
    desc: 'Strict hygiene protocols, at home.\nDoctor-reviewed where required. Nurse-administered.\nBook in 60 seconds.',
    subtext: 'Prescription or doctor consult required',
    color: '#f37721',
  },
  {
    num: '02',
    icon: Truck,
    title: 'Safety Dispatch',
    desc: 'Cold-chain verified vaccines are prepared and a certified nurse is assigned. Real-time tracking from dispatch to your doorstep.',
    color: '#673c96',
  },
  {
    num: '03',
    icon: ShieldCheck,
    title: 'Professional Vaccination',
    desc: 'Qualified Nurse-administered care at home. Includes a monitored 30-minute post-vaccination observation period for complete safety.',
    color: '#3e195e',
  },
  {
    num: '04',
    icon: Lock,
    title: 'Digital Vault',
    desc: 'Instant vaccination certificate, detailed receipt, and permanent digital health records — all securely stored in your app.',
    color: '#311b52',
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" style={{ paddingTop: '24px', paddingBottom: '64px' }} className="bg-[#fdfcff]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: 'Montserrat' }}>
            The Seamless Journey
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mt-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            4 Steps to Comfortable, In-Home Care
          </h2>
          <p
            data-testid="how-journey-line"
            className="mt-3 text-sm text-[#3e195e]/75 max-w-2xl mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            A journey designed around reassurance — Discover · Book · Prepare · Vaccinate · Record.
          </p>
          <p className="text-base text-[#3e195e]/70 mt-4 max-w-xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            From consultation to digital records — a curated healthcare experience
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {phases.map((phase, i) => (
            <motion.div
              key={phase.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="relative group"
              data-testid={`phase-${i + 1}`}
            >
              <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#673c96]/8 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 h-full">
                {/* Phase number */}
                <div className="flex items-center gap-4 mb-6">
                  <span
                    className="text-3xl font-bold tracking-tight opacity-20"
                    style={{ fontFamily: 'Poppins', color: phase.color }}
                  >
                    {phase.num}
                  </span>
                  {/* Equidistant vertical line */}
                  <div className="w-px h-8 mx-2" style={{ backgroundColor: `${phase.color}30` }} />
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${phase.color}10` }}>
                    <phase.icon className="w-5 h-5" style={{ color: phase.color }} />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-[#311b52] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {phase.title}
                </h3>
                <p className="text-sm text-[#673c96]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', whiteSpace: 'pre-line' }}>
                  {phase.desc}
                </p>
                {phase.subtext && (
                  <p className="text-[11px] text-[#673c96]/60 italic mt-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {phase.subtext}
                  </p>
                )}
              </div>

              {/* Connection line between cards (hidden on last) */}
              {i < 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-gradient-to-r from-[#673c96]/15 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
